import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Target,
  User,
  Trophy,
  Users,
  Kanban,
  TrendingUp,
  FolderOpen,
  Archive,
  CheckSquare,
  Calendar,
  BarChart3,
  Settings,
  LogOut,
  FileBarChart,
  History,
  Link as LinkIcon,
  Mail,
} from 'lucide-react';
import { AawtaLogo } from '../AawtaLogo';
import { useAuth } from '../../context/AuthContext';
import { ROLE_LABELS } from '../../lib/utils';

const NAV = [
  { to: '/', icon: LayoutDashboard, label: 'Executive Dashboard', testid: 'nav-executive' },
  { to: '/leads', icon: Users, label: 'Partnership CRM', testid: 'nav-leads' },
  { to: '/pipeline', icon: Kanban, label: 'Sales Pipeline', testid: 'nav-pipeline' },
  { to: '/my-dashboard', icon: User, label: 'Team Dashboard', testid: 'nav-personal' },
  { to: '/leaderboard', icon: Trophy, label: 'Leaderboard', testid: 'nav-leaderboard' },
  { to: '/sales', icon: TrendingUp, label: 'Sales Dashboard', testid: 'nav-sales' },
  { to: '/analytics', icon: BarChart3, label: 'Partnership Analytics', testid: 'nav-analytics' },
  { to: '/reports', icon: FileBarChart, label: 'Reports', testid: 'nav-reports' },
  { to: '/resources', icon: FolderOpen, label: 'Resource Center', testid: 'nav-resources' },
  { to: '/assets', icon: Archive, label: 'Asset Tracker', testid: 'nav-assets' },
  { to: '/tasks', icon: CheckSquare, label: 'Task Management', testid: 'nav-tasks' },
  { to: '/calendar', icon: Calendar, label: 'Calendar', testid: 'nav-calendar' },
  { to: '/booking', icon: LinkIcon, label: 'Book with Me', testid: 'nav-booking' },
  { to: '/communications', icon: Mail, label: 'Email Campaigns', testid: 'nav-communications' },
  { to: '/goals', icon: Target, label: 'KPI Goals', testid: 'nav-goals', adminOnly: true },
  { to: '/member-goals', icon: Target, label: 'Member Goals', testid: 'nav-member-goals', adminOnly: true },
  { to: '/audit-log', icon: History, label: 'Audit Log', testid: 'nav-audit-log', adminOnly: true },
  { to: '/settings', icon: Settings, label: 'Admin Settings', testid: 'nav-settings', adminOnly: true },
];

export function Sidebar() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside
      className="w-72 hidden md:flex flex-col bg-navy text-white h-screen fixed top-0 left-0 z-40"
      data-testid="sidebar"
    >
      <div className="px-6 py-6 border-b border-white/10">
        <AawtaLogo size={42} dark showText />
      </div>

      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        <div className="space-y-0.5">
          {NAV.filter((n) => !n.adminOnly || isAdmin).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all relative ${
                  isActive
                    ? 'bg-white/10 text-white before:absolute before:left-0 before:top-2 before:bottom-2 before:w-1 before:bg-magenta before:rounded-r'
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`
              }
            >
              <n.icon className="w-4 h-4 shrink-0" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="px-3 py-4 border-t border-white/10">
        <div className="px-3 py-2 mb-2">
          <div className="text-sm font-semibold truncate">{user?.full_name}</div>
          <div className="text-[11px] text-white/60 uppercase tracking-wider">
            {ROLE_LABELS[user?.role] || 'Team'}
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="sidebar-logout"
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-white/70 hover:bg-magenta hover:text-white transition-all"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </aside>
  );
}
